<template>
  <b-modal
    :title="modalTitle"
    dialog-class="modal-lg"
    :modelValue="merchant !== null"
    body-class="pt-0"
    @update:modelValue="(open) => !open && hide()"
  >
    <template #default>
      <ConfirmModal ref="confirmDelete"></ConfirmModal>
      <LoaderWrapper ref="loader">
        <div v-if="merchant" class="row">
          <div class="col-sm-12 mb-3">
            <div class="row g-2 align-items-start">
              <div class="col-md-8">
                <FormInputWrapper v-slot="parentProps" :errors="getErrors('ip')">
                  <input
                    v-model="newIp"
                    type="text"
                    class="form-control form-control-sm"
                    :class="parentProps.class"
                    :placeholder="helper.trans('labels.merchant_ip_whitelist_placeholder')"
                    @keydown.enter.prevent="addIp"
                  />
                </FormInputWrapper>
              </div>
              <div class="col-md-4">
                <button type="button" class="btn btn-sm btn-outline-primary text-capitalize-first" @click="addIp">
                  {{ helper.trans('button.add') }}
                </button>
              </div>
            </div>
          </div>
          <div class="col-sm-12">
            <table class="table table-striped border-light align-middle text-left text-nowrap position-relative mb-0">
              <thead>
                <tr>
                  <th class="text-capitalize-first">{{ helper.trans('labels.ip_address') }}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="row in entries" :key="row.id">
                  <td>{{ row.ip }}</td>
                  <td class="text-end">
                    <button type="button" class="btn btn-sm btn-outline-danger text-nowrap" @click="remove(row)">
                      <i class="fa fa-trash"></i>
                    </button>
                  </td>
                </tr>
                <tr v-if="entries.length === 0">
                  <td colspan="2" class="text-secondary">{{ helper.trans('labels.merchant_ip_whitelist_empty') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </LoaderWrapper>
    </template>
    <template #footer>
      <button type="button" class="btn btn-outline-secondary btn-sm text-capitalize-first" @click="hide">
        {{ helper.trans('button.close') }}
      </button>
    </template>
  </b-modal>
</template>

<script lang="ts">
import {computed, defineComponent, ref} from 'vue';
import {BModal} from 'bootstrap-vue-3';
import {helper} from "@/service/CommonHelper";
import APIClient from "@/service/APIClient";
import LoaderWrapper from "@/components/LoaderWrapper.vue";
import FormInputWrapper from "@/components/FormInputWrapper.vue";
import ConfirmModal, {ConfirmModalProps} from "@/components/ConfirmModal.vue";
import MerchantDTO from "@/models/MerchantDTO";
import MerchantIpWhitelistEntryDTO from "@/models/MerchantIpWhitelistEntryDTO";
import ValidationErrorDTO from "@/models/ValidationErrorDTO";

const api = new APIClient()

export default defineComponent({
  components: {
    BModal,
    LoaderWrapper,
    FormInputWrapper,
    ConfirmModal
  },
  emits: ['updated'],
  setup(_, {emit}) {
    const loader = ref<InstanceType<typeof LoaderWrapper> | null>(null)
    const confirmDelete = ref<InstanceType<typeof ConfirmModal> | null>(null)
    const merchant = ref<MerchantDTO | null>(null)
    const entries = ref<MerchantIpWhitelistEntryDTO[]>([])
    const newIp = ref('')
    const errors = ref<Record<string, ValidationErrorDTO[]>>({})

    const modalTitle = computed(() =>
      merchant.value
        ? helper.trans('labels.merchant_ip_whitelist_title', {name: merchant.value.name})
        : ''
    )

    const getErrors = (path: string) => errors.value[path] ?? []

    const applyValidationErrors = (data: ValidationErrorDTO[]) => {
      const next: Record<string, ValidationErrorDTO[]> = {}
      for (const err of data) {
        if (!next[err.field]) {
          next[err.field] = []
        }
        next[err.field].push(err)
      }
      errors.value = next
    }

    const fetchEntries = async () => {
      if (!merchant.value) {
        return
      }
      entries.value = await api.merchantIpWhitelistList(merchant.value.id)
    }

    const loadList = async () => {
      loader.value?.show()
      try {
        await fetchEntries()
      } finally {
        loader.value?.hide()
      }
    }

    const addIp = async () => {
      if (!merchant.value) {
        return
      }
      errors.value = {}
      const ip = newIp.value.trim()
      if (!ip) {
        return
      }
      loader.value?.show()
      try {
        await api.merchantIpWhitelistCreate(merchant.value.id, {ip})
        newIp.value = ''
        await fetchEntries()
        emit('updated')
      } catch (e: any) {
        if (e.response && e.request && e.request.status === 400 && e.response.data && Array.isArray(e.response.data)) {
          applyValidationErrors(e.response.data)
        } else {
          console.log(e)
        }
      } finally {
        loader.value?.hide()
      }
    }

    const remove = (row: MerchantIpWhitelistEntryDTO) => {
      if (!merchant.value) {
        return
      }
      const m = merchant.value
      confirmDelete.value?.show({
        title: helper.trans('labels.delete_confirm'),
        okTitle: helper.trans('button.delete'),
        cancelTitle: null,
        onOkClick: async () => {
          loader.value?.show()
          try {
            await api.merchantIpWhitelistDelete(m.id, row.id)
            await fetchEntries()
            emit('updated')
          } finally {
            loader.value?.hide()
          }
        }
      } as ConfirmModalProps)
    }

    const show = async (m: MerchantDTO) => {
      merchant.value = m
      newIp.value = ''
      errors.value = {}
      await loadList()
    }

    const hide = () => {
      merchant.value = null
      newIp.value = ''
      errors.value = {}
      entries.value = []
    }

    return {
      helper,
      loader,
      confirmDelete,
      merchant,
      entries,
      newIp,
      modalTitle,
      getErrors,
      addIp,
      remove,
      show,
      hide
    }
  }
})
</script>
